<script setup lang="ts">
import { type Component, provide, type VNode } from 'vue'
import { Toast, Toaster, createToaster, type ToastOptions } from '@ark-ui/vue'
import { toasterInjectionKey } from '../composables/useToaster'
import { Check, CircleSlash, Info, LoaderCircle, TriangleAlert, X } from 'lucide-vue-next'

export interface UiToasterProps {
    placement?: 'top-start' | 'top' | 'top-end' | 'bottom-start' | 'bottom' | 'bottom-end'
    max?: number
    gap?: number
    overlap?: boolean
    duration?: number
    teleportTo?: string | HTMLElement
    topOffset?: string
}

const props = withDefaults(defineProps<UiToasterProps>(), {
    placement: 'bottom-end',
    max: 3,
    gap: 16,
    overlap: true,
    duration: 5000,
    teleportTo: 'body',
})

const toaster = createToaster({ placement: props.placement, max: props.max, gap: props.gap, overlap: props.overlap })
provide(toasterInjectionKey, toaster)

defineSlots<{
    toast(props: { toast: ToastOptions }): VNode[]
    default(): VNode[]
}>()

function getIconForType(type?: string) {
    const map: Record<string, Component> = {
        success: Check,
        error: CircleSlash,
        warning: TriangleAlert,
        info: Info,
        loading: LoaderCircle,
    }
    return (type && map[type]) || LoaderCircle
}

function getViewportStyle() {
    if (!props.topOffset || !props.placement.startsWith('top')) {
        return undefined
    }

    return {
        top: props.topOffset,
    }
}
</script>

<template>
    <Teleport :to="props.teleportTo" defer>
        <Toaster v-if="toaster" :toaster="toaster" :style="getViewportStyle()" v-slot="toast">
            <Toast.Root
                class="pointer-events-auto w-90 rounded-lg se-transition-all font-archivo"
                :style="{
                    transform: 'translate(var(--x), var(--y)) scale(var(--scale))',
                    opacity: 'var(--opacity)',
                    height: 'var(--height)'
                }" :class="toast.meta?.theme === 'light'
                    ? 'relative overflow-hidden border border-gray-200 bg-white text-gray-900 shadow-xl shadow-black/8'
                    : {
                        'bg-se-dark text-se-white p-4 border border-se-success': toast.type === 'success',
                        'bg-se-dark text-se-white p-4 border border-se-danger': toast.type === 'error',
                        'bg-se-dark text-se-white p-4 border border-amber-500': toast.type === 'warning',
                        'bg-se-dark text-se-white p-4 border border-se-highlight': toast.type === 'info',
                        'bg-se-dark text-se-white p-4 border border-se-gray-300': toast.type === 'loading'
                    }">
                <slot name="toast" :toast="toast">
                    <div class="flex-1 flex flex-col">
                        <div class="flex items-center justify-between max-w-full gap-2">
                            <component :is="getIconForType(toast.type)" class="shrink-0 w-4 h-4" :class="{
                                'text-se-danger': toast.type === 'warning',
                                'text-se-success': toast.type === 'success',
                                'text-se-highlight': toast.type === 'info',
                                'text-se-gray-300': toast.type === 'loading',
                                'animate-spin': toast.type === 'loading'
                            }" />
                            <Toast.Title class="se-font-p flex-1 font-semibold"> {{ toast.title }}</Toast.Title>
                            <Toast.CloseTrigger
                                class="se-transition-all shrink-0 bg-se-white/15 rounded-tr-lg rounded-bl-lg p-1 cursor-pointer w-6 h-6 translate-x-4 -translate-y-4 hover:bg-se-white/20 flex items-center justify-center">
                                <X class="h-3 w-3 shrink-0 text-se-white" aria-hidden="true" />
                            </Toast.CloseTrigger>
                        </div>
                        <div class="flex gap-2 justify-between">
                            <Toast.Description v-if="toast.description" class="se-font-h6 text-se-white">
                                {{ toast.description }}
                            </Toast.Description>
                            <Toast.ActionTrigger v-if="toast.action" @click="toast.action.onClick"
                                class="se-font-p-small font-medium text-se-highlight hover:text-se-highlight/80 se-transition-all shrink-0">
                                {{ toast.action.label }}
                            </Toast.ActionTrigger>
                        </div>
                    </div>
                </slot>
                <Toast.CloseTrigger
                    v-if="toast.meta?.theme === 'light'"
                    class="se-transition-all absolute right-0 top-0 z-10 flex h-6 w-6 items-center justify-center rounded-tr-lg rounded-bl-lg bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600">
                    <X class="h-3 w-3 shrink-0" aria-hidden="true" />
                </Toast.CloseTrigger>
            </Toast.Root>
        </Toaster>
    </Teleport>
    <slot />
</template>
